import React from 'react';
import { ExclamationCircleFill } from 'react-bootstrap-icons';

import './styles.css';
import { Col, Row } from 'react-bootstrap';
import FilterBadge from './FilterBadge';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import {
  PatientSearchFilters,
  SearchFilterType,
  type QuickFilter,
} from '../../types';
import {
  currentSearchFilterInputNeeded,
  currentSearchFilterRemoteOptionsNeeded,
  generateFilterOption,
  isChildAgeFilters,
  isDoNothingSelect,
  isSingleSelectionChildFilter,
} from '../../utils';
import { debounce } from 'lodash';
import { SelectionStatus } from '../../hooks';
import Swal from 'sweetalert2';
import { validateAge } from '../../validator/filtersValidator';

interface QuickFiltersProps {
  quickFilters: QuickFilter[];
  currentFilter: QuickFilter | null;
  onSelectFilter: (filter: QuickFilter) => void;
  searchText?: string;
  isFetching?: boolean;
  fetchRemoteFilters: (
    filter: QuickFilter,
    searchText?: string,
  ) => Promise<void>;
  endFlow: () => void;
  continueNext: (searchText: string) => void;
  mode: SelectionStatus;
  ageFilterInvalid?: boolean;
  selectedParentFilter?: QuickFilter;
  selectedFilters: QuickFilter[];
}
const QuickFilters = ({
  quickFilters,
  selectedParentFilter,
  onSelectFilter,
  isFetching,
  currentFilter,
  searchText,
  fetchRemoteFilters,
  endFlow,
  continueNext,
  mode,
  selectedFilters,
}: QuickFiltersProps) => {
  const currentFlow = currentFilter?.type || null;
  const isSpecificUnitLocalFiltering =
    mode === SelectionStatus.MULTIPLE &&
    selectedParentFilter?.filterKey === 'SpecificUnit';
  const ageNeedInput =
    mode === SelectionStatus.AGE &&
    currentFilter?.type === SearchFilterType.NO_SELECTION;
  const specificPatientInputNeeded = mode === SelectionStatus.SPECIFIC_PATIENT;
  const doNext = () => {
    if (!searchText) return;
    if (mode === SelectionStatus.AGE) {
      if (
        !validateAge([
          ...selectedFilters,
          generateFilterOption(
            searchText,
            PatientSearchFilters.AgeFilter + '-' + searchText,
          ),
        ])
      ) {
        Swal.fire({
          icon: 'warning',
          text: 'Please enter a valid age range',
          showConfirmButton: true,
        });
        return;
      }
    }
    continueNext(searchText);
  };
  const tips = React.useMemo(() => {
    if (
      isSpecificUnitLocalFiltering &&
      searchText &&
      quickFilters.length === 0
    ) {
      return 'No matching services';
    }
    if (
      (currentFlow && currentSearchFilterInputNeeded(currentFlow)) ||
      ageNeedInput ||
      specificPatientInputNeeded
    ) {
      return 'Enter a keyword to search';
    }
    return 'No more quick filters';
  }, [
    currentFlow,
    isSpecificUnitLocalFiltering,
    searchText,
    quickFilters.length,
    ageNeedInput,
    specificPatientInputNeeded,
  ]);

  const displayedQuickFilters = React.useMemo(() => {
    if (!isSpecificUnitLocalFiltering) {
      return quickFilters;
    }
    const normalizedSearch = (searchText || '').trim().toLowerCase();
    if (!normalizedSearch) {
      return quickFilters;
    }
    return quickFilters.filter((filter) =>
      String(filter.label).toLowerCase().includes(normalizedSearch),
    );
  }, [isSpecificUnitLocalFiltering, quickFilters, searchText]);

  const debouncedFetchRemoteFilters = React.useCallback(
    debounce((filter: QuickFilter, text?: string) => {
      fetchRemoteFilters(filter, text);
    }, 800),
    [],
  );

  React.useEffect(() => {
    (async () => {
      if (mode === SelectionStatus.MULTIPLE && selectedParentFilter) {
        if (isChildAgeFilters(selectedParentFilter)) {
          return;
        }
        if (selectedParentFilter.filterKey === 'SpecificUnit') {
          return;
        }
        debouncedFetchRemoteFilters(selectedParentFilter, searchText);
      } else if (
        currentFilter &&
        !isDoNothingSelect(currentFilter.filterKey as PatientSearchFilters)
      ) {
        if (currentFilter.filterKey === 'SpecificPatient') {
          endFlow();
          return;
        }

        if (
          isChildAgeFilters(currentFilter) ||
          isSingleSelectionChildFilter(currentFilter) ||
          !currentSearchFilterRemoteOptionsNeeded(currentFilter.type)
        ) {
          return;
        }

        debouncedFetchRemoteFilters(currentFilter, searchText);
      }
    })();
  }, [searchText, currentFlow, fetchRemoteFilters, mode]);

  return (
    <div>
      <Row className="quick-filter-row">
        <Col className="quick-filter-container position-relative" sm={10}>
          <div className="d-flex align-items-center justify-content-between">
            <span className="quick-filter-banner">
              <ExclamationCircleFill color="#78C6D7" size={14} /> Combine Quick
              filter options and keywords to create your search
            </span>
            {mode === SelectionStatus.MULTIPLE &&
              currentFilter?.type &&
              currentFilter?.type === SearchFilterType.NO_SELECTION && (
                <button
                  className="multi-selection-done-button"
                  onClick={endFlow}
                >
                  <span>Done</span>
                </button>
              )}
            {mode === SelectionStatus.AGE && (
              <div className="d-flex gap-2">
                <button
                  className="multi-selection-done-button"
                  onClick={doNext}
                >
                  <span>Next</span>
                </button>
                <button
                  className="multi-selection-done-button"
                  onClick={() => {
                    doNext();
                    endFlow();
                  }}
                >
                  <span>Finish</span>
                </button>
              </div>
            )}
          </div>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <span className="quick-filter-label">Quick filters:</span>
            {isFetching && (
              <LoadingSpinner color="#5c416b" size={16} showText="" />
            )}
            {displayedQuickFilters.length > 0 ? (
              displayedQuickFilters.map((filter) => (
                <FilterBadge
                  key={filter.id}
                  filter={filter}
                  onClick={onSelectFilter}
                />
              ))
            ) : (
              <span className="quick-filter-label">{tips}</span>
            )}
          </div>
        </Col>
        <Col sm={2} />
      </Row>
    </div>
  );
};

export default QuickFilters;
